import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Pencil, Trash2, Users as UsersIcon, Search } from 'lucide-react';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { Team, Manager, Role } from '@/types';
import { TeamForm } from './TeamForm';
import { DeleteTeamDialog } from './DeleteTeamDialog';

interface TeamsTabProps {
  teams: Team[];
  managers: Manager[];
  roles: Role[];
  onAddTeam: (teamData: Omit<Team, 'id'>) => Promise<void>;
  onUpdateTeam: (teamId: string, updates: Partial<Team>) => Promise<void>;
  onDeleteTeam: (teamId: string) => Promise<void>;
  isLoading: boolean;
}

const TeamsTab: React.FC<TeamsTabProps> = ({ teams, managers, roles, onAddTeam, onUpdateTeam, onDeleteTeam, isLoading }) => {
  const [isAddingTeam, setIsAddingTeam] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | undefined>(undefined);
  const [deletingTeam, setDeletingTeam] = useState<Team | null>(null);
  const [teamTypeFilter, setTeamTypeFilter] = useState<'ALL' | 'SALES' | 'INTERNAL_PM' | 'INSTALLER' | 'BIZ_DEV'>('ALL');
  const [searchTerm, setSearchTerm] = useState('');

  const handleAddTeam = async (teamData: Omit<Team, 'id'>) => {
    try {
      await onAddTeam(teamData);
      setIsAddingTeam(false);
    } catch (error) {
      console.error('Error adding team:', error);
      throw error; // Re-throw to allow TeamForm to handle
    }
  };

  const handleUpdateTeam = async (teamData: Omit<Team, 'id'>) => {
    if (!editingTeam) return;
    try {
      await onUpdateTeam(editingTeam.id, teamData);
      setEditingTeam(undefined);
    } catch (error) {
      console.error('Error updating team:', error);
      throw error; // Re-throw to allow TeamForm to handle
    }
  };

  const handleDeleteTeam = async () => {
    if (!deletingTeam) return;
    try {
      await onDeleteTeam(deletingTeam.id);
      setDeletingTeam(null);
    } catch (error) {
      console.error('Error deleting team:', error);
    }
  };

  const filteredTeams = teams.filter(team => {
    const matchesType = teamTypeFilter === 'ALL' || team.type === teamTypeFilter;
    const matchesSearch = team.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesSearch;
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-end items-center">
        <div className="flex items-center space-x-4">
          <SearchableSelect
            value={teamTypeFilter}
            onValueChange={(value: string) => setTeamTypeFilter(value as 'ALL' | 'SALES' | 'INTERNAL_PM' | 'INSTALLER' | 'BIZ_DEV')}
            options={[
              { label: 'All Teams', value: 'ALL' },
              { label: 'Sales', value: 'SALES' },
              { label: 'Internal PM', value: 'INTERNAL_PM' },
              { label: 'Installer', value: 'INSTALLER' },
              { label: 'Business Development', value: 'BIZ_DEV' }
            ]}
            placeholder="Filter by team type"
            className="w-[180px] h-10"
          />
          <Input
            placeholder="Search teams..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-48"
          />
          {/* Hidden button that can be triggered from parent */}
          <Button
            onClick={() => setIsAddingTeam(true)}
            className="hidden"
            data-add-team-button
          >
            <UsersIcon className="mr-2 h-4 w-4" />
            Add Team
          </Button>
        </div>
      </div>


      <TeamForm
        initialData={editingTeam ? {
          id: editingTeam.id,
          name: editingTeam.name,
          type: editingTeam.type,
          manager_id: editingTeam.manager_id || '',
          biz_dev_id: editingTeam.biz_dev_id || '',
          manager_name: editingTeam.manager_name || '',
          biz_dev_rep_name: editingTeam.biz_dev_rep_name || '',
          empower_created: editingTeam.empower_created || false,
          ses_created: editingTeam.ses_created || false
        } : undefined}
        onSubmit={editingTeam ? handleUpdateTeam : handleAddTeam}
        onCancel={() => {
          setIsAddingTeam(false);
          setEditingTeam(undefined);
        }}
        isSubmitting={false}
        managers={managers}
        roles={roles}
        open={isAddingTeam || !!editingTeam}
        onOpenChange={(open) => {
          if (!open) {
            setIsAddingTeam(false);
            setEditingTeam(undefined);
          }
        }}
      />

      <div className="overflow-hidden border rounded-lg shadow-sm">
        <div className="overflow-x-auto">
          <Table className="min-w-full">
            <TableHeader className="bg-muted">
              <TableRow>
                <TableHead className="font-semibold text-muted-foreground">Name</TableHead>
                <TableHead className="font-semibold text-muted-foreground">Type</TableHead>
                <TableHead className="font-semibold text-muted-foreground">Manager</TableHead>
                <TableHead className="font-semibold text-muted-foreground">Biz Dev Rep</TableHead>
                <TableHead className="font-semibold text-muted-foreground">Empower Status</TableHead>
                <TableHead className="font-semibold text-muted-foreground">SES Status</TableHead>
                <TableHead className="text-right font-semibold text-muted-foreground">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="bg-card">
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Loading teams...
                  </TableCell>
                </TableRow>
              ) : filteredTeams.length > 0 ? (
                filteredTeams.map((team) => (
                  <TableRow key={team.id} className="hover:bg-muted">
                    <TableCell className="font-medium">
                      <span className="text-sm text-foreground">{team.name}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">{team.type.replace('_', ' ')}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">{team.manager_name}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">{team.biz_dev_rep_name}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">{team.empower_created ? 'Created' : 'Not Created'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">{team.ses_created ? 'Created' : 'Not Created'}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-primary hover:bg-muted"
                        onClick={() => setEditingTeam(team)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="ml-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        onClick={() => setDeletingTeam(team)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    {teams.length > 0 ? 'No matching teams found.' : 'No teams found. Create your first team to get started.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <DeleteTeamDialog
        open={!!deletingTeam}
        onOpenChange={(open) => !open && setDeletingTeam(null)}
        onConfirm={handleDeleteTeam}
        teamName={deletingTeam?.name || ''}
      />
    </div>
  );
};

export default TeamsTab;